import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import { ServicingAnalysisGranularityEnum, TemplateCategory } from "@app/templates";
import { AdequacyDetail } from "@app/projects/network-assessment/adequacy/adequacyDetail";
import { AdequacyAggregate } from "@app/projects/network-assessment/adequacy/adequacyAggregate";
import { NetworkAssessmentAnalysisType, ProjectRunConfigurationType } from "@app/projects/enumerators";
import Project from "@app/projects/project";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { SpecialtyGroupExceptionStatus } from "@app/exceptions/enums";
import React from "react";
import Roles from "@app/user/roles";
import { UserInfo } from "@app/user/UserContext";

export const getServicingGranularityHeader = (servicingGranularity: ServicingAnalysisGranularityEnum): string => {
    switch (servicingGranularity) {
        case ServicingAnalysisGranularityEnum.State:
            return "In State";
        case ServicingAnalysisGranularityEnum.Region:
        case ServicingAnalysisGranularityEnum.Unknown:
            return "In Region";
        case ServicingAnalysisGranularityEnum.County:
        default:
            return "In County";
    }
}

export const getMarketAnalysisTooltip = (projectRunConfiguration: ProjectRunConfiguration): string => {
    let tooltipMesage = "using the Market provider selection.";
    const useNetworkProviders = projectRunConfiguration.useNetworkProvidersForMarketAnalysis;
    const useProspectProviders = projectRunConfiguration.useProspectProvidersForMarketAnalysis;

    if (useNetworkProviders && useProspectProviders) {
        tooltipMesage = "the aggregate of Network, Prospect and Market provider selections.";
    } else if (useNetworkProviders) {
        tooltipMesage = "the aggregate of Network and Market provider selections.";
    } else if (useProspectProviders) {
        tooltipMesage = "the aggregate of Prospect and Market provider selections.";
    }

    return "Market Analysis results reflect " + tooltipMesage;
}

export const getFillableGapsIndicatorTooltip = (projectRunConfiguration: ProjectRunConfiguration): string => {
    let tooltipMessage = "";
    const network = !!projectRunConfiguration.useNetworkProvidersForMarketAnalysis;
    const prospect = !!projectRunConfiguration.useProspectProvidersForMarketAnalysis;
    const market = !!projectRunConfiguration.useMarketProvidersForImpact;

    switch (true) {
        case (!network && !prospect && market):
            tooltipMessage = "a single provider impact from either the Prospect or Market Providers.";
            break;
        case (!network && prospect && !market):
            tooltipMessage = "the impact using the aggregation of Network and Prospect Providers.";
            break;
        case (!network && prospect && market):
            tooltipMessage = "the impact using the aggregation of Market and Prospect Providers.";
            break;
        case (network && !prospect && market):
            tooltipMessage = "the impact using the aggregation of Network and Market Providers.";
            break;
        case (network && prospect && !market):
            tooltipMessage = "the impact using the aggregation of Network and Prospect Providers.";
            break;
        case (network && prospect && market):
            tooltipMessage = "the impact using the aggregation of Network, Prospect and Market Providers.";
            break;
        // odd case where there are prospect providers but they aren't used for market analysis
        // network providers don't matter here
        case ((!!projectRunConfiguration.prospectProviderSetId && !prospect) && !market):
            tooltipMessage = "the impact using the aggregation of Network and Prospect Providers."
            break;
        // any other case, the column should be hidden anyway
    }

    return "Fillable gap indicator results reflect " + tooltipMessage;
}

export const dataHasExceptions = (data: AdequacyDetail[] | AdequacyAggregate[]): boolean => {
    if (!!data && data[0]?.hasOwnProperty("exceptionCount")) {
        return (data as AdequacyAggregate[]).some((d) => d.exceptionCount > 0);
    }

    return (data as AdequacyDetail[])?.some((d) => (!!d?.exception && !d.exception.isUnprocessed
        && (d.exception.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Appealed
            || d.exception.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Approved
            || d.exception.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Denied
            || d.exception.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Pending
            || d.exception.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Submitted)));
}

export const dataHasGaps = (data: AdequacyDetail[] | AdequacyAggregate[], cmsCreditIsApplying: boolean): boolean => {
    if (!!data && data[0]?.hasOwnProperty("exceptionCount")) {
        return (data as AdequacyAggregate[]).some((d) => d.gapCount > 0 || (cmsCreditIsApplying && d.gapCountCMSCredit > 0));
    }

    return (data as AdequacyDetail[])?.some((d) => !!d && d.accessSummary.memberCount > 0 && !(d.metCriteria || (d.applicationCreditMetCriteria && cmsCreditIsApplying)));
}

export const showPatternOfCare = (user: UserInfo, enabledFeatureFlags: Set<string>, isComparingResults: boolean, project: Project, runConfiguration: ProjectRunConfigurationResult, records?: AdequacyDetail[] | AdequacyAggregate[], hasExceptions?: boolean, cmsCreditIsApplying?: boolean): boolean => {
    return user.roles.includes(Roles.PATTERN_OF_CARE_USER)
        && !isComparingResults
        && (runConfiguration.isLatestPublished || !runConfiguration.datePublished)
        && project.showPatternOfCareProviders
        && !runConfiguration.isProjected && !runConfiguration.isProjectedTermed
        && runConfiguration.networkAssessmentAnalysisType !== NetworkAssessmentAnalysisType.Opportunity
        && ((!!records && (dataHasGaps(records, cmsCreditIsApplying) || dataHasExceptions(records))) || hasExceptions);
}

export const getPrimaryAndSecondaryRunConfigurations = (projectRunConfigurations: ProjectRunConfiguration[], currentRunConfigurationId: number): { primaryRunConfiguration: ProjectRunConfiguration, secondaryRunConfiguration?: ProjectRunConfiguration } => {

    const selected = projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === currentRunConfigurationId);
    const companion = projectRunConfigurations.find((prc) => !!currentRunConfigurationId && prc.companionProjectRunConfigurationId === currentRunConfigurationId);

    if (selected.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental) {
        return { primaryRunConfiguration: companion, secondaryRunConfiguration: selected };
    }

    return { primaryRunConfiguration: selected, secondaryRunConfiguration: companion };
}

export const getTemplateSpecialtyCategories = (runConfiguration: ProjectRunConfigurationResult, compareToRunConfiguration?: ProjectRunConfigurationResult): TemplateCategory[] => {
    if (!runConfiguration)
        return [];

    if (!!compareToRunConfiguration
        && new Date(compareToRunConfiguration.dateCreated).getTime() > new Date(runConfiguration.dateCreated).getTime()) {
        return compareToRunConfiguration.templateCategories;
    }

    return runConfiguration.templateCategories;
}

export const getZipPrimaryCountyTooltip = (): React.ReactElement => {
  return React.createElement(
    React.Fragment,
    null,
    "Counts are aggregated by zip code within the service area.",
    React.createElement("br", null),
    "The Primary County designation is reflective of the zip code population centroid assignment."
  );
};